import { useState, useMemo } from 'react';
import { Search, Sparkles, BookOpen, CircleCheck as CheckCircle2, Clock, RefreshCw, GraduationCap, Phone, PhoneCall, Mail, TrendingUp, Layers, Target, Handshake, Map, Crown, Users, Megaphone, ChartBar as BarChart3, Cpu, Globe, Award, Flag, ExternalLink, ChevronDown, ChevronUp, Loader as Loader2, CircleAlert as AlertCircle, ChevronRight } from 'lucide-react';
import type { CraftCategoryRecord, CraftModuleRecord, UserCraftProgress, DifficultyLevel } from '../../../types/craft';
import { CRAFT_ROLES, CRAFT_SEGMENTS } from '../../../types/craft';

const ICON_MAP: Record<string, typeof BookOpen> = {
  GraduationCap, Phone, PhoneCall, Mail, Search, TrendingUp, Layers, Target,
  Handshake, Map, Crown, Users, Megaphone, BarChart3, Cpu, Globe, Award,
  Flag, RefreshCw, ExternalLink, BookOpen,
};

const DIFFICULTY_COLORS: Record<DifficultyLevel, { bg: string; text: string }> = {
  foundational: { bg: 'bg-app-positive/10', text: 'text-app-positive' },
  intermediate: { bg: 'bg-app-focus/10', text: 'text-app-focus' },
  advanced: { bg: 'bg-app-warning/10', text: 'text-app-warning' },
};

const STATUS_CONFIG = {
  completed: { label: 'Ready', color: 'text-app-positive', icon: CheckCircle2 },
  processing: { label: 'Generating...', color: 'text-app-warning', icon: Loader2 },
  error: { label: 'Error', color: 'text-app-critical', icon: AlertCircle },
  none: { label: 'Awaiting generation', color: 'text-app-muted', icon: Sparkles },
};

interface Props {
  categories: CraftCategoryRecord[];
  modules: CraftModuleRecord[];
  progress: UserCraftProgress[];
  loading: boolean;
  isAdmin: boolean;
  onSelectModule: (slug: string) => void;
  onGenerate: (slug: string) => void;
}

export default function CraftLibraryHome({ categories, modules, progress, loading, isAdmin, onSelectModule, onGenerate }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<DifficultyLevel | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [segmentFilter, setSegmentFilter] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(categories.map(c => c.slug)));

  const totalReady = modules.filter(m => m.status === 'completed').length;
  const totalProcessing = modules.filter(m => m.status === 'processing').length;

  const filteredModules = useMemo(() => {
    return modules.filter(m => {
      if (difficultyFilter !== 'all' && m.difficulty_level !== difficultyFilter) return false;
      if (roleFilter !== 'all' && !(m.applicable_roles as string[])?.includes(roleFilter)) return false;
      if (segmentFilter !== 'all' && !(m.applicable_segments as string[])?.includes(segmentFilter)) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const inTitle = m.title.toLowerCase().includes(q);
        const inSubtitle = m.subtitle.toLowerCase().includes(q);
        const inDesc = m.description.toLowerCase().includes(q);
        const inTags = (m.tags as string[])?.some(t => t.toLowerCase().includes(q));
        if (!inTitle && !inSubtitle && !inDesc && !inTags) return false;
      }
      return true;
    });
  }, [modules, difficultyFilter, roleFilter, segmentFilter, searchQuery]);

  const toggleCategory = (slug: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-app-line-strong border-t-forest-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="bg-app-surface rounded-lg border border-app-line p-8 mb-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-app-focus" />
            <span className="text-xs font-semibold uppercase tracking-wider text-app-focus">Applied sales mastery</span>
          </div>
          <h2 className="text-xl font-bold text-app-ink mb-2">My Craft Intelligence Library</h2>
          <p className="text-sm text-app-muted leading-relaxed mb-4">
            A self-evolving, expert-grade library of sales knowledge spanning {categories.length} domains
            and {modules.length} deep learning modules. Each module provides 10 dimensions of structured
            intelligence, from core concepts and field application to frameworks, practice drills and
            quarterly evolution updates.
          </p>
          <div className="flex items-center gap-4 text-xs text-app-muted">
            <span className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5" />
              {modules.length} modules
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {totalReady} ready
            </span>
            {totalProcessing > 0 && (
              <span className="flex items-center gap-1 text-app-warning">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {totalProcessing} generating
              </span>
            )}
            <span className="flex items-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" />
              Quarterly evolution cycle
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search modules..."
            className="w-full pl-10 pr-4 py-2.5 border border-app-line rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
          />
        </div>

        <select
          value={difficultyFilter}
          onChange={e => setDifficultyFilter(e.target.value as DifficultyLevel | 'all')}
          className="px-3 py-2.5 border border-app-line rounded-lg text-xs text-app-muted focus:outline-none focus:ring-2 focus:ring-forest-500"
        >
          <option value="all">All Levels</option>
          <option value="foundational">Foundational</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>

        <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="px-3 py-2.5 border border-app-line rounded-lg text-xs text-app-muted focus:outline-none focus:ring-2 focus:ring-forest-500"
        >
          <option value="all">All Roles</option>
          {CRAFT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select
          value={segmentFilter}
          onChange={e => setSegmentFilter(e.target.value)}
          className="px-3 py-2.5 border border-app-line rounded-lg text-xs text-app-muted focus:outline-none focus:ring-2 focus:ring-forest-500"
        >
          <option value="all">All Segments</option>
          {CRAFT_SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-6">
        {categories.map(cat => {
          const catModules = filteredModules.filter(m => m.category_slug === cat.slug);
          if (catModules.length === 0 && searchQuery) return null;
          const isExpanded = expandedCategories.has(cat.slug);
          const CatIcon = ICON_MAP[cat.icon_name] || BookOpen;

          return (
            <div key={cat.slug} className="border border-app-line rounded-lg overflow-hidden bg-app-surface">
              <button
                onClick={() => toggleCategory(cat.slug)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-app-canvas transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-app-focus/10 flex items-center justify-center flex-shrink-0">
                    <CatIcon className="w-4.5 h-4.5 text-app-focus" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold text-app-ink">{cat.title}</h3>
                    <p className="text-xs text-app-muted">{cat.module_count} modules</p>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
              </button>

              {isExpanded && (
                <div className="px-5 pb-5 border-t border-app-line">
                  <p className="text-xs text-app-muted pt-3 pb-4">{cat.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catModules.map(mod => (
                      <ModuleCard
                        key={mod.slug}
                        module={mod}
                        progress={progress}
                        isAdmin={isAdmin}
                        onSelect={() => onSelectModule(mod.slug)}
                        onGenerate={() => onGenerate(mod.slug)}
                      />
                    ))}
                    {catModules.length === 0 && (
                      <p className="text-xs text-app-muted col-span-full py-4">No modules match your filters.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModuleCard({ module: mod, progress, isAdmin, onSelect, onGenerate }: {
  module: CraftModuleRecord;
  progress: UserCraftProgress[];
  isAdmin: boolean;
  onSelect: () => void;
  onGenerate: () => void;
}) {
  const status = mod.status;
  const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.none;
  const StatusIcon = config.icon;
  const canClick = status === 'completed';
  const isProcessing = status === 'processing';
  const diffStyle = DIFFICULTY_COLORS[mod.difficulty_level];

  const moduleProgress = progress.filter(p => p.module_slug === mod.slug);
  const completedTabs = moduleProgress.filter(p => p.status === 'completed').length;

  return (
    <div
      className={`rounded-lg border p-4 transition group ${
        canClick ? 'border-app-line hover:shadow-none hover:border-app-focus/30 cursor-pointer' : 'border-app-line'
      }`}
      onClick={() => canClick && onSelect()}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className={`text-sm font-semibold text-app-ink leading-snug flex-1 ${canClick ? 'group-hover:text-app-focus' : ''} transition-colors`}>
          {mod.title}
        </h4>
        {canClick && (
          <ChevronRight className="w-4 h-4 text-app-line-strong group-hover:text-app-focus flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition" />
        )}
      </div>

      <p className="text-xs text-app-muted leading-relaxed mb-3 line-clamp-2">{mod.subtitle}</p>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${diffStyle.bg} ${diffStyle.text}`}>
          {mod.difficulty_level}
        </span>
        <span className="text-[10px] text-app-muted flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {mod.estimated_duration_minutes}m
        </span>
        {mod.version > 0 && mod.quarter_label && (
          <span className="text-[10px] text-app-muted">{mod.quarter_label}</span>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-2">
        <StatusIcon className={`w-3 h-3 ${config.color} ${isProcessing ? 'animate-spin' : ''}`} />
        <span className={`text-xs ${config.color}`}>{config.label}</span>
      </div>

      {completedTabs > 0 && canClick && (
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-1 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${mod.title} completion`} aria-valuemin={0} aria-valuemax={10} aria-valuenow={Math.min(completedTabs, 10)}>
            <div className="h-full bg-app-positive rounded-full" style={{ width: `${Math.min((completedTabs / 10) * 100, 100)}%` }} />
          </div>
          <span className="text-[10px] text-app-muted">{completedTabs}/10</span>
        </div>
      )}

      {isProcessing && (
        <div className="mt-1">
          <div className="flex items-center gap-2 text-xs text-app-warning mb-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Stage {mod.current_stage || 0} of 3
          </div>
          <div className="w-full h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${mod.title} generation`} aria-valuemin={0} aria-valuemax={3} aria-valuenow={mod.current_stage || 0}>
            <div className="h-full bg-app-warning rounded-full transition duration-700" style={{ width: `${((mod.current_stage || 0) / 3) * 100}%` }} />
          </div>
        </div>
      )}

      {isAdmin && !isProcessing && (status === 'none' || status === 'error') && (
        <button
          onClick={e => { e.stopPropagation(); onGenerate(); }}
          className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-forest-50 text-forest-700 hover:bg-forest-100 border border-forest-200 transition-colors"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {status === 'error' ? 'Retry' : 'Generate'}
        </button>
      )}

      {isAdmin && canClick && (
        <button
          onClick={e => { e.stopPropagation(); onGenerate(); }}
          className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-app-muted hover:text-forest-700 hover:bg-forest-50 border border-transparent hover:border-forest-200 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Regenerate
        </button>
      )}

      {mod.error_message && status === 'error' && (
        <p className="text-xs text-app-critical mt-2 line-clamp-2">{mod.error_message}</p>
      )}
    </div>
  );
}
